/*
 * scrapeSSE — minimal browser SSE client for the Zoltar Demo Pilot scraper.
 *
 * The scraper speaks standard Server-Sent Events (event: message \n data: {...} \n\n).
 * The browser's built-in EventSource does not support POST bodies or custom
 * headers, so we stream the response ourselves via fetch() + ReadableStream +
 * TextDecoder and parse SSE frames out of the accumulating buffer.
 *
 * Usage:
 *   const ctl = new AbortController();
 *   for await (const evt of scrapeStream({ url, params, token, signal: ctl.signal })) {
 *     // evt = { event: 'message', data: { statusCode, message, data? } }
 *     if (evt.data?.statusCode === 'done') { ... }
 *   }
 *
 * The generator terminates when the server closes the stream OR when the
 * abort signal fires (the fetch rejects, which propagates out of the
 * generator as an AbortError — callers should catch it).
 */

import { ZOLTAR_BASE_URL, ZOLTAR_SCRAPE_SSE_PATH } from '../config';

function parseFrame(raw) {
  // One SSE frame. Lines:
  //   event: message
  //   data: { ... JSON ... }
  // data can span multiple `data:` lines per spec — we concatenate with \n.
  let eventType = 'message';
  const dataLines = [];
  for (const rawLine of raw.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (!line) continue;
    if (line.startsWith(':')) continue; // comment/heartbeat
    if (line.startsWith('event:')) {
      eventType = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
    // `id:` and `retry:` are valid SSE fields but we don't use them.
  }
  if (dataLines.length === 0) return null;
  const dataStr = dataLines.join('\n');
  let data;
  try {
    data = JSON.parse(dataStr);
  } catch {
    data = dataStr; // keep raw string if the server emitted non-JSON
  }
  return { event: eventType, data };
}

export async function* scrapeStream({ url, params, token, signal }) {
  if (!url) throw new Error('scrapeStream: `url` is required');
  if (!token) throw new Error('scrapeStream: `token` (IMS bearer) is required');

  const endpoint = `${ZOLTAR_BASE_URL}${ZOLTAR_SCRAPE_SSE_PATH}`;
  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ url, params: params || {} }),
    signal,
  });

  if (!resp.ok) {
    // Drain body for the error message but cap it — error payloads can be huge.
    const bodyText = await resp.text().catch(() => '');
    const snippet = bodyText ? `: ${bodyText.slice(0, 500)}` : '';
    const err = new Error(`Scraper returned HTTP ${resp.status}${snippet}`);
    err.status = resp.status;
    throw err;
  }

  if (!resp.body || typeof resp.body.getReader !== 'function') {
    throw new Error('Scraper response has no readable body stream (browser lacks ReadableStream support?)');
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        // Flush any trailing frame that wasn't terminated by \n\n.
        if (buffer.trim()) {
          const parsed = parseFrame(buffer);
          if (parsed) yield parsed;
        }
        return;
      }
      buffer += decoder.decode(value, { stream: true });

      // Frames are separated by a blank line, i.e. \n\n (or \r\n\r\n).
      // Split on both.
      while (true) {
        const nn = buffer.indexOf('\n\n');
        const rn = buffer.indexOf('\r\n\r\n');
        let sepIdx = -1;
        let sepLen = 0;
        if (nn !== -1 && (rn === -1 || nn < rn)) { sepIdx = nn; sepLen = 2; }
        else if (rn !== -1) { sepIdx = rn; sepLen = 4; }
        if (sepIdx === -1) break;

        const frame = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + sepLen);
        const parsed = parseFrame(frame);
        if (parsed) yield parsed;
      }
    }
  } finally {
    try { reader.releaseLock(); } catch (_) { /* ignore */ }
  }
}

/**
 * Run a full scrape and return the list of scraped image assets from the
 * final `done` event. Yields intermediate events to the `onProgress` callback
 * so UIs can show the current status line without owning the iteration loop.
 *
 * Returns `{ assets, texts, status }` where:
 *   - assets: array of image entries { src, alt, width, height, isLogo, ... }
 *   - texts: array of scraped semantic texts (when requested)
 *   - status: the terminal status message (e.g. "Done")
 *
 * Throws AbortError if cancelled.
 */
export async function runScrape({ url, params, token, signal, onProgress }) {
  let lastStatus = '';
  let finalPayload = null;

  for await (const evt of scrapeStream({ url, params, token, signal })) {
    const payload = evt.data;
    if (!payload || typeof payload !== 'object') continue;

    if (typeof payload.message === 'string') {
      lastStatus = payload.message;
    }
    if (typeof onProgress === 'function') {
      try { onProgress(payload); } catch (_) { /* never let UI throw kill the stream */ }
    }
    if (payload.statusCode === 'done') {
      finalPayload = payload.data || null;
      break;
    }
  }

  const assetsRaw = (finalPayload && Array.isArray(finalPayload.assets)) ? finalPayload.assets : [];
  // Filter to image-origin assets (the done payload also contains color palettes
  // with originType === 'scraper' but data.mime === 'application/x-pallete').
  const images = assetsRaw
    .map((a) => (a && a.data) ? a.data : null)
    .filter((d) => d && typeof d.src === 'string' && d.src && !(d.mime && String(d.mime).includes('pallete')));

  const texts = (finalPayload && Array.isArray(finalPayload.texts)) ? finalPayload.texts : [];

  const toneOfVoice = (finalPayload && typeof finalPayload.toneOfVoice === 'string') ? finalPayload.toneOfVoice.trim() : '';
  const brandGuidelines = (finalPayload && typeof finalPayload.brandGuidelines === 'string') ? finalPayload.brandGuidelines.trim() : '';
  const pageDescription = (finalPayload && typeof finalPayload.pageDescription === 'string') ? finalPayload.pageDescription.trim() : '';
  const brandColors = (finalPayload && Array.isArray(finalPayload.brandColors)) ? finalPayload.brandColors : [];

  return { images, texts, toneOfVoice, brandGuidelines, pageDescription, brandColors, status: lastStatus };
}

/**
 * Group the scraper's flat `texts` array (Array<{text, component, originUrl}>)
 * into the shape TextsTab renders: { [component]: string[] }.
 *
 * - Drops empty/whitespace-only strings.
 * - De-dupes within a component (the scraper sometimes emits repeats).
 * - Preserves first-seen order so the accordion stays stable across rescans.
 */
export function groupTextsByComponent(textsArray) {
  const out = {};
  const seen = {}; // component -> Set of texts
  for (const entry of textsArray || []) {
    if (!entry || typeof entry.text !== 'string') continue;
    const text = entry.text.trim();
    if (!text) continue;
    const component = (typeof entry.component === 'string' && entry.component.trim()) || 'other';
    if (!out[component]) {
      out[component] = [];
      seen[component] = new Set();
    }
    if (seen[component].has(text)) continue;
    seen[component].add(text);
    out[component].push(text);
  }
  return out;
}

/**
 * Merge two grouped-texts shapes: `{ [component]: string[] }`.
 * For each component, append `incoming` strings to `existing`, dropping
 * duplicates. Preserves first-seen order of `existing` then `incoming`.
 * Returns a new object — does not mutate either input.
 */
export function mergeTextsByComponent(existing, incoming) {
  const out = { ...(existing || {}) };
  for (const [component, items] of Object.entries(incoming || {})) {
    const prev = Array.isArray(out[component]) ? out[component] : [];
    const seen = new Set(prev);
    const merged = prev.slice();
    for (const t of items || []) {
      if (typeof t !== 'string') continue;
      if (seen.has(t)) continue;
      seen.add(t);
      merged.push(t);
    }
    out[component] = merged;
  }
  return out;
}
